<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >

    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('LIQUIDATE_FIXED_DEPOSIT_ACCOUNT')"
            :label="$t('Liquidate FD')"
            size="md"
            class="mt-0"
            @click="goToFdLiquidationAccount"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>

    </div>

    <b-table
      ref="customerInfoRef"
      :items="fdLiquidationList"
      responsive
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(financialDate)="data">
        {{ toDateWithYear(data.value) }}
      </template>
      <template #cell(transactionDate)="data">
        {{ toDateWithCustomTime(data.value) }}
      </template>

      <template #cell(amount)="data">
        {{ toBalance(data.value) }}
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchfdLiquidation"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter Account Name')"
            type="text"
            onpaste="return false;"
            :is-allow-special-characters="false"
          />
        </b-col>
        <!-- Account Name -->

        <!-- FD Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('FD Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- FD Account Number -->

        <!-- Liquidation Date From -->
        <b-col md="6">
          <DatePickerField
            id="liquidation-date-from"
            v-model="filter.financialDateFrom"
            :label="$t('Liquidation Date (From)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Liquidation Date From-->

        <!-- Liquidation Date To -->
        <b-col md="6">
          <DatePickerField
            id="liquidation-date-to"
            v-model="filter.financialDateTo"
            :label="$t('Liquidation Date (To)')"
            :config="datePickerConfig"
          />
        </b-col>
        <!-- Liquidation Date To-->
        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :placeholder="$t('Select Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
          />
          <!-- Branch -->
        </b-col>
      </b-row>
    </b-modal>
    <!-- Filter modal -->

    <FullScreenSpinner :show="showSpinner" />
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable,
  BSpinner,
} from 'bootstrap-vue'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { mapActions, mapGetters } from 'vuex'
import TablePagination from '@/views/components/table/TablePagination.vue'
import { toAmount, toBalance } from '@/helpers/formatAmount'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import { toDateWithYear, toDateWithTime, toDateWithCustomTime } from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import DatePickerField from '../../../../forms/DatePickerField.vue'
import Button from '../../../../components/button/Button.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    FullScreenSpinner,
    DatePickerField,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      toDateWithTime,
      toDateWithCustomTime,
      toAmount,
      toBalance,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'FD Account Number' },
        { key: 'branch', sortable: true, label: 'Branch Name' },
        { key: 'amount', sortable: true, label: 'Liquidated Amount' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Ref No.' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument No.' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'initiatedBy', sortable: true, label: 'Initiated By' },
      ],

      fdLiquidationList: [],
      filter: {
        accountName: null,
        accountNumber: null,
        branchCode: null,
        financialDateFrom: null,
        financialDateTo: null,
        entryCode: 'D-FDPL',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
      },
      showSpinner: false,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchfdLiquidation()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchfdLiquidation()
    },
  },
  mounted() {
    this.fetchfdLiquidation()
  },
  methods: {
    ...mapActions({
      getFdLiquidationReport: 'reports/getTransactionReports',
    }),
    goToFdLiquidationAccount() {
      this.$router.push('add-fixed-deposit-liquidation')
    },
    async fetchfdLiquidation() {
      try {
        this.isBusy = true
        const response = await this.getFdLiquidationReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          route: 'postingsReport',
          permission: 'VIEW_LIQUIDATE_FIXED_DEPOSIT',
        })
        const { data } = response
        if (data.data !== null) {
          this.isBusy = false
          this.fdLiquidationList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },

    resetFilters() {
      this.filter = {
        ...null,
      }
    },
    resetFilter() {
      this.filter = {
        ...null,
        entryCode: 'D-FDPL',
      }
      this.fetchfdLiquidation()
    },
  },
}
</script>
